import { APPROVED_INTERNAL_MCP_SERVERS } from "@/pages/GovernancePage/applications/policies/getDemoData"

import type {
  Agent,
  PolicyRecord
} from "@/pages/GovernancePage/applications/policies/getDemoData.types"
import type { AiPolicyFormRuleGroup } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/AiPolicyForm/AiPolicyForm.types"

export const HEADING_KEY = "___heading"
export const ALL_AGENTS_HEADING_KEY = `${HEADING_KEY}_1`
export const ENVIRONMENT_POLICIES_HEADING_KEY = `${HEADING_KEY}_2`
export const AGENT_POLICIES_HEADING_KEY = `${HEADING_KEY}_3`
export const ALL_MCP_SERVERS_HEADING_KEY = `${HEADING_KEY}_4`
export const MCP_SERVER_POLICIES_HEADING_KEY = `${HEADING_KEY}_5`

// Maps the rule groups edited in AiPolicyForm back to stored PolicyRecords.
// Rules without a condition or value are the trailing empty editor rows.
export const toRuleGroups = (
  groups: AiPolicyFormRuleGroup[] | undefined
): PolicyRecord[] =>
  (groups ?? []).flatMap((group) => {
    const rules = (group.rules ?? []).flatMap((rule) =>
      rule.condition && rule.value && rule.value.length > 0
        ? [{ id: rule.condition, value: [rule.value].flat() }]
        : []
    )

    if (rules.length === 0 || !group.actions) return []

    return [{ rules, actions: group.actions }]
  })

export const getData = <A extends Agent>(agents: {
  all: A[]
  playground: A[]
  agents: { [key in string]: A }
}) => [
  {
    id: "1",
    key: ALL_AGENTS_HEADING_KEY,
    scopeId: "1",
    name: "All agents policy",
    type: "heading" as const,
    count: 1,
    total: 1,
    overrides: ""
  },
  {
    id: "11",
    key: "11",
    expandable: true,
    scopeId: "11",
    name: "All agents",
    count: agents.all.length,
    total: 21,
    overrides: "",
    description: `${agents.all.length} agents`,
    type: "scope" as const,
    policyId: 1,
    policyName: "No Kimi 2.7",
    policyDescription:
      "No agent may run on Kimi 2.7 or call the code_execution tool. A change that introduces either is prevented in development, and the AI governance channel is notified.",
    violated: false,
    agentsDescription: `${agents.all.length} agents run these rules. 1 agent is exempt through an override`,
    agents: agents.all,
    allowed: [],
    notAllowed: [
      {
        actions: {
          enabled: true,
          enforceRuleOnRuntime: false,
          preventViolationOnDevelopment: false,
          notifyOnViolation: true
        },
        rules: [{ id: "modelName", value: ["Kimi 2.7"] }]
      }
    ]
  },
  {
    id: ENVIRONMENT_POLICIES_HEADING_KEY,
    key: ENVIRONMENT_POLICIES_HEADING_KEY,
    scopeId: "2",
    name: "Environments policy",
    type: "heading" as const,
    count: 1,
    total: 1,
    overrides: ""
  },
  {
    id: "2.1",
    expandable: true,
    key: "2.1",
    scopeId: "2.1",
    name: "Playground",
    count: agents.playground.length,
    total: agents.playground.length,
    overrides: "",
    description: `${agents.playground.length} agents`,
    type: "scope" as const,
    subtype: "environment" as const,
    policyId: 2,
    policyName: "Approved MCP servers only",
    policyDescription:
      "Agents in playground may connect only to the approved MCP servers. A call to any other server blocks the session and notifies the AI governance channel.",
    allowed: [
      {
        actions: {
          enabled: true,
          enforceRuleOnRuntime: false,
          preventViolationOnDevelopment: false,
          notifyOnViolation: true
        },
        rules: [{ id: "mcpServer", value: APPROVED_INTERNAL_MCP_SERVERS }]
      }
    ],
    notAllowed: [],
    violated: false,
    agentsDescription: `${agents.playground.length} agents in this environment run these rules.`,
    agents: agents.playground
  },
  {
    id: AGENT_POLICIES_HEADING_KEY,
    key: AGENT_POLICIES_HEADING_KEY,
    scopeId: "3",
    name: "Agent specific policy",
    type: "heading" as const,
    count: 1,
    total: 1,
    overrides: ""
  },
  {
    id: agents.agents.RemediationLead?.id,
    key: agents.agents.RemediationLead?.id,
    scopeId: agents.agents.RemediationLead?.id,
    name: agents.agents.RemediationLead?.name,
    count: 7,
    total: 7,
    overrides: "POL-AA-1",
    category: "Waiver",
    grantedBy: "platform-eng",
    expires: "2026-12-31",
    description: "7 agents",
    type: "agent" as const,
    policyId: 2,
    policyName: "RemediationLead can't use Kimi 3.0",
    policyDescription:
      "RemediationLead runs remediation scripts through code_execution. For this agent the All agents policy is replaced by one that keeps the Kimi 2.7 ban and only notifies.",
    allowed: [],
    notAllowed: [
      {
        actions: {
          enabled: true,
          enforceRuleOnRuntime: false,
          preventViolationOnDevelopment: false,
          notifyOnViolation: true
        },
        rules: [{ id: "modelName", value: ["Kimi 3.0"] }]
      }
    ],
    violated: false,
    agentsDescription: "7 agents in this environment run these rules."
  }
]
